// Host detection for platform-marketing pages (pricing, signup, landing).
//
// These pages MUST only render on the platform marketing site (nexuscart.co).
// When a tenant subdomain or custom domain (e.g. luxurygive.com) serves them,
// the tenant chrome (header/footer/banner) bleeds through and confuses the
// visitor — they think it's the merchant's own pricing page, not the SaaS
// pricing.
//
// Behavior:
// - Dev hosts (replit.dev, .repl.co, localhost): treated AS platform host so
//   the pages remain testable locally.
// - nexuscart.co, www.nexuscart.co, app.nexuscart.co: platform host. Render.
// - Anything else (tenant subdomain or custom domain): NOT platform host.
//   Caller should redirect to https://nexuscart.co<path>.

const PLATFORM_HOSTS = new Set([
  "nexuscart.co",
  "www.nexuscart.co",
  "app.nexuscart.co",
]);

const PLATFORM_ORIGIN = "https://nexuscart.co";

// Canonical PRODUCTION platform hosts ONLY (nexuscart.co / www / app) — does
// NOT include dev/preview hosts. These domains are, by definition, the SaaS
// marketing site, so the host kind can be resolved to "platform" WITHOUT
// trusting a live API call. This keeps nexuscart.co rendering the landing page
// even when the API is briefly unreachable (e.g. mid-deploy cold start),
// instead of falling through to a tenant storefront shell.
export function isCanonicalPlatformHost(host: string = window.location.host): boolean {
  return PLATFORM_HOSTS.has(host.toLowerCase());
}

export function isPlatformHost(host: string = window.location.host): boolean {
  const h = host.toLowerCase();
  if (PLATFORM_HOSTS.has(h)) return true;
  // Dev: Replit preview + localhost
  if (
    h.endsWith(".replit.dev") ||
    h.endsWith(".replit.app") ||
    h.endsWith(".repl.co") ||
    h.startsWith("localhost") ||
    h.startsWith("127.0.0.1")
  ) {
    return true;
  }
  return false;
}

// Hard-redirect the browser to the same path on the platform marketing site.
// Preserves search params (e.g. ?package=2&cycle=yearly&ref=AFF-XYZ).
export function redirectToPlatform(path: string = window.location.pathname): void {
  const search = window.location.search || "";
  const hash = window.location.hash || "";
  window.location.replace(`${PLATFORM_ORIGIN}${path}${search}${hash}`);
}

// Synchronous guard — call at the top of a component (before any hooks that
// depend on the page being on the platform host). If we're not on a platform
// host, kicks off the redirect immediately and returns true so the caller
// can short-circuit rendering. This avoids a flash of the tenant chrome
// wrapping the platform page while a useEffect-based redirect would wait
// until after the first paint.
let _redirectFired = false;
export function guardPlatformHostSync(path: string = window.location.pathname): boolean {
  if (typeof window === "undefined") return false;
  if (isPlatformHost()) return false;
  if (!_redirectFired) {
    _redirectFired = true;
    redirectToPlatform(path);
  }
  return true;
}
